import SuperTokensLock from "browser-tabs-lock";

import {daUtils} from '../../_utils';
import {authenticationApi, IAuthUriResponse, IAuthTokenResponse, IBoolResponse} from "../../_api";

// import { verify as verifyIdToken } from './jwt';
import {CacheKey, ICache, InMemoryCache, LocalStorageCache} from './cache';
import { AuthenticationError, TimeoutError } from './errors';

import {ClientStorage, CookieStorage, SessionStorage} from './storage';

import {
  DEFAULT_AUTH_CLIENT, DEFAULT_SCOPE, DEFAULT_SESSION_CHECK_EXPIRY_DAYS,
  MISSING_REFRESH_TOKEN_ERROR_MESSAGE, INVALID_REFRESH_TOKEN_ERROR_MESSAGE
} from './constants';

import {
  AuthClientOptions,
  AuthUser,
  BaseLoginOptions,
  CacheLocation,
  GetUserOptions,
  LogoutOptions,
  LogoutUrlOptions,
  OAuthTokenOptions,
  RedirectLoginOptions,
  RedirectLoginResult,
  User,
  GetAccessTokenOptions,
  RefreshTokenOptions, AuthLoginOptions, AuthenticationResult,
  AuthSignupOptions, AuthSignupResult,
  AuthConfirmSignupOptions, AuthConfirmSignupResult,
  AuthPasswordResetOptions, AuthPasswordResetResult,
  AuthConfirmPasswordResetOptions, AuthConfirmPasswordResetResult,
} from './global';
import { StateManager } from "./stateManager";
import { singlePromise, retryPromise } from "./promise-utils";

/**
 * @ignore
 */
const lock = new SuperTokensLock();

/**
 * @ignore
 */
const GET_ACCESS_TOKEN_LOCK_KEY = 'da-auth.lock.getAccessToken';

// /**
//  * @ignore
//  */
// const cacheLocationBuilders: Record<string, () => ICache> = {
//   memory: () => new InMemoryCache().enclosedCache,
//   localstorage: () => new LocalStorageCache()
// };
//
// /**
//  * @ignore
//  */
// const cacheFactory = (location: string) => {
//   return cacheLocationBuilders[location];
// };

/**
 * @ignore
 */
const cacheFactory = (location: CacheLocation): (() => ICache) | undefined => {
  switch (location) {
    case CacheLocation.LocalStorage:
      return () => new LocalStorageCache();
      break;
    case CacheLocation.Memory:
      return () => new InMemoryCache().enclosedCache;
      break;
    default:
      return undefined;
      break;
  }
};

// /**
//  * @ignore
//  */
// const getTokenIssuer = (issuer: string, domainUrl: string) => {
//   if (issuer) {
//     return issuer.startsWith('https://') ? issuer : `https://${issuer}/`;
//   }
//
//   return `${domainUrl}/`;
// };

/**
 * @ignore
 */
const getCustomInitialOptions = (
  options: AuthClientOptions
): BaseLoginOptions => {
  const {
    advancedOptions,
    audience,
    authClient,
    authorizeTimeoutInSeconds,
    cacheLocation,
    client_id,
    domain,
    issuer,
    leeway,
    max_age,
    redirect_uri,
    scope,
    useRefreshTokens,
    ...customParams
  } = options;
  return customParams;
};

/**
 * Auth0 SDK for Single Page Applications using [Authorization Code Grant Flow with PKCE](https://auth0.com/docs/api-auth/tutorials/authorization-code-grant-pkce).
 */
export default class AuthClient {
  private cache: ICache;
  private stateManager: StateManager;
  // private customOptions: BaseLoginOptions;
  private clientId: string;
  private audience: string;
  private domainUrl: string;
  // private tokenIssuer: string;
  private defaultScope: string;
  private scope: string;
  private cookieStorage: ClientStorage;
  private sessionCheckExpiryDays: number;

  cacheLocation: CacheLocation;

  private authProvider: string = '';

  constructor(private options: AuthClientOptions) {
    // typeof window !== 'undefined' && validateCrypto();
    this.cacheLocation = options.cacheLocation || CacheLocation.Memory;

    this.cookieStorage = CookieStorage;

    this.sessionCheckExpiryDays =
      options.sessionCheckExpiryDays || DEFAULT_SESSION_CHECK_EXPIRY_DAYS;

    const factory = cacheFactory(this.cacheLocation);
    if (factory === undefined) {
      throw new Error(`Invalid cache location "${this.cacheLocation}"`);
    }
    this.cache = factory();

    const stateStorageLocation = options.useCookiesForState
      ? this.cookieStorage
      : SessionStorage;

    this.clientId = options.client_id;
    this.audience = options.audience ? options.audience : 'unknown';
    this.domainUrl = `https://${options.domain}`;
    this.defaultScope = DEFAULT_SCOPE;
    this.scope = options.scope || '' || DEFAULT_SCOPE;
    this.stateManager = new StateManager(stateStorageLocation);
  }

  private _url(path: string) {
    const authClient = encodeURIComponent(
        daUtils.encodeB64(JSON.stringify(this.options.authClient || DEFAULT_AUTH_CLIENT))
    );
    return `${this.domainUrl}${path}&authClient=${authClient}`;
  }


  private _parseNumber(value: any): number | undefined {
    if (typeof value !== 'string') {
      return value;
    }
    return parseInt(value, 10) || undefined;
  }

  /**
   * ```js
   * const url = await authClient.getAuthorizeUrl();
   * ```
   *
   * Gets an `/oauth/authorize` URL from Django Api
   * for use by loginWithRedirect()
   *
   */
  async getAuthorizeUrl(options: RedirectLoginOptions): Promise<IAuthUriResponse> {
    try {
      const { appState, ...authorizeOptions } = options;

      const stateIn = daUtils.encodeB64(daUtils.createRandomString());
      const nonceIn = daUtils.encodeB64(daUtils.createRandomString());

      const resp = await authenticationApi.get_auth_url(options)

      if(resp !== undefined && resp.uri) {
        const newState = Object.assign(appState, resp);

        this.stateManager.create({
          nonce: nonceIn,
          scope: this.scope,
          audience: this.audience,
          appState: newState,
        });

        return resp;
      }

      throw new Error('Unable to get Authorize Uri from Api server')

    } catch (error) {
      //Network error
      throw error;
    }
  }

  /**
   * ```js
   * const user = await authClient.getUser();
   * ```
   *
   * Returns the user information if available.
   *
   * If you provide an audience or scope, they should match an existing Access Token
   * (the SDK stores a corresponding UserInfo object with every Access Token, and uses the
   * scope and audience to look up the cache entry)
   *
   * @typeparam TUser The type to return, has to extend {@link User}. Defaults to {@link User} when omitted.
   * @param options
   */
  public async getUser(
    options: GetUserOptions = {}
  ): Promise<AuthUser | undefined> {
    const audience = options.audience || this.audience || 'default';
    const scope = options.scope || this.scope || this.defaultScope;

    const cache_entry = this.cache.get(
      new CacheKey({
        client_id: this.clientId,
        audience,
        scope
      })
    );

    return cache_entry && cache_entry.user_info;
  }

  /**
   * ```js
   * const url = await authClient.getAuthorizeUrl();
   * ```
   *
   * Gets an `/oauth/authorize` URL from Django Api
   * for use by loginWithRedirect()
   *
   */
  async login(options: AuthLoginOptions): Promise<any> {
    try {
      // const appState = {};
      const { appState, ...authorizeOptions } = options;

      const stateIn = daUtils.encodeB64(daUtils.createRandomString());
      const nonceIn = daUtils.encodeB64(daUtils.createRandomString());

      const resp = await authenticationApi.login(authorizeOptions)

      if(resp !== undefined) {
        // Check if this is a NEW_PASSWORD_REQUIRED challenge
        if (resp.success === false && resp.data?.challenge_name === 'NEW_PASSWORD_REQUIRED') {
          return {
            challenge: 'NEW_PASSWORD_REQUIRED',
            session: resp.data.session,
            username: resp.data.username,
          };
        }

        // Normal success case
        if (resp.success === true && resp.data) {
          const newState = Object.assign(appState, resp.data);

          this.stateManager.create({
            nonce: nonceIn,
            scope: this.scope,
            audience: this.audience,
            appState: newState,
          });

          return resp.data;
        }
      }

      throw new Error('Username does not exist or password is invalid')

    } catch (error) {
      //Network error
      throw error;
    }
  }

  /**
   * ```js
   * await authClient.loginWithPassword(options);
   * ```
   *
   * Performs a redirect to `/authorize` using the parameters
   * provided as arguments. Random and secure `state` and `nonce`
   * parameters will be auto-generated.
   *
   * @param options
   */
  public async loginWithPassword(options: AuthLoginOptions = {}): Promise<AuthenticationResult> {
    let resp = null;

    try {
      const authResult = await this.login(options);

      // Check if this is a NEW_PASSWORD_REQUIRED challenge
      if (authResult?.challenge === 'NEW_PASSWORD_REQUIRED') {
        resp = {
          state: "new_password_required",
          session: authResult.session,
          username: authResult.username,
        };
        return resp;
      }

      const cache_entry = {
        audience: this.audience,
        scope: this.scope,
        client_id: this.clientId,
        access_token: authResult?.access_token!,
        expires_in: authResult?.expires_in!,
        refresh_token: authResult?.refresh_token,
        user_info: authResult?.user_info
      };

      this.cache.save(cache_entry);

      this.cookieStorage.save('da-auth.is.authenticated', true, {
        daysUntilExpire: this.sessionCheckExpiryDays
      });

      resp = {
        state: "success",
        user: authResult?.user_info,
      };
    } catch (error) {
      //Network error
      let msg = "";

      if (typeof error === "string") {
          msg = error // works, `e` narrowed to string
      } else if (error instanceof Error) {
          msg = error.message // works, `e` narrowed to Error
      }

      resp = {
        state: "error",
        error: msg,
      };
    }

    return resp;
  }

  /**
   * ```js
   * await authClient.loginWithRedirect(options);
   * ```
   *
   * Performs a redirect to `/authorize` using the parameters
   * provided as arguments. Random and secure `state` and `nonce`
   * parameters will be auto-generated.
   *
   * @param options
   */
  public async loginWithRedirect(options: RedirectLoginOptions = {}) {
    const resp = await this.getAuthorizeUrl(options);
    window.location.assign(resp.uri);
  }

  /**
   * After the browser redirects back to the callback page,
   * call `handleRedirectCallback` to handle success and error
   * responses from auth provider. If the response is successful, results
   * will be valid according to their expiration times.
   */
  public async handleRedirectCallback(
    url: string = window.location.href
  ): Promise<RedirectLoginResult> {
    const queryStringFragments = url.split('?').slice(1);

    if (queryStringFragments.length === 0) {
      throw new Error('There are no query params available for parsing.');
    }

    const { state, code, error, error_description } = daUtils.parseQueryResult(
      queryStringFragments.join('')
    );

    const stateObject = this.stateManager.get();

    // StateObject should have a `code_verifier` to do PKCE for CSRF protection
    if (!stateObject) {
      throw new Error('Invalid application state');
    }

    this.stateManager.remove();

    if (error) {
      throw new AuthenticationError(
          error,
          error_description,
          state,
          stateObject.appState,
      );
    }

    if(stateObject.appState && stateObject.appState.provider)
      this.authProvider = stateObject.appState.provider;

    const tokenOptions = {
      code: code,
      audience: this.audience,
      scope: this.scope,
      email: stateObject.appState.email,
      provider: stateObject.appState.provider
    } as OAuthTokenOptions;

    const authResult = await authenticationApi.get_auth_token(tokenOptions);

    const cache_entry = {
      audience: this.audience,
      scope: this.scope,
      client_id: this.clientId,
      access_token: authResult?.access_token!,
      expires_in: authResult?.expires_in!,
      refresh_token: authResult?.refresh_token,
      user_info: authResult?.user_info
    };

    this.cache.save(cache_entry);

    this.cookieStorage.save('da-auth.is.authenticated', true, {
      daysUntilExpire: this.sessionCheckExpiryDays
    });

    return {
      appState: stateObject.appState
    };
  }

  /**
   * ```js
   * await authClient.checkSession();
   * ```
   *
   * Check if the user is logged in using `getTokenSilently`. The difference
   * with `getTokenSilently` is that this doesn't return a token, but it will
   * pre-fill the token cache.
   *
   * This method also heeds the `da-auth.is.authenticated` cookie, as an optimization
   * to prevent calling Auth unnecessarily. If the cookie is not present because
   * there was no previous login (or it has expired) then tokens will not be refreshed.
   *
   * It should be used for silently logging in the user when you instantiate the
   * `AuthClient` constructor. You should not need this if you are using the
   * `createAuthClient` factory.
   *
   * @param options
   */
  public async checkSession(options?: GetAccessTokenOptions) {
    if (!this.cookieStorage.get('da-auth.is.authenticated')) {
      return;
    }

    try {
      await this.getAccessToken(options);
    } catch (error) {
      // @ts-ignore
      if (!RECOVERABLE_ERRORS.includes(error.error)) {
        throw error;
      }
    }
  }

  /**
   * ```js
   * const token = await authClient.getAccessToken(options);
   * ```
   *
   * If there's a valid token stored, return it. Otherwise, opens an
   * iframe with the `/authorize` URL using the parameters provided
   * as arguments. Random and secure `state` and `nonce` parameters
   * will be auto-generated. If the response is successful, results
   * will be valid according to their expiration times.
   *
   * If refresh tokens are used, the token endpoint is called directly with the
   * 'refresh_token' grant. If no refresh token is available to make this call,
   * the SDK falls back to using an iframe to the '/authorize' URL.
   *
   * This method may use a web worker to perform the token call if the in-memory
   * cache is used.
   *
   * If an `audience` value is given to this function, the SDK always falls
   * back to using an iframe to make the token exchange.
   *
   * Note that in all cases, falling back to an iframe requires access to
   * the `auth0` cookie.
   *
   * @param options
   */
  public async getAccessToken(options: GetAccessTokenOptions = {}) {
    const { ignoreCache, ...getTokenOptions } = {
      audience: this.options.audience,
      ignoreCache: false,
      ...options,
      scope: options.scope || this.defaultScope
    };

    return singlePromise(
      () =>
        this._getAccessToken({
          ignoreCache,
          ...getTokenOptions
        }),
      `${this.options.client_id}::${getTokenOptions.audience}::${getTokenOptions.scope}`
    );
  }

  private async _getAccessToken(options: GetAccessTokenOptions = {}) {
    const { ignoreCache, ...getTokenOptions } = options;

    const getAccessTokenFromCache = () => {
      const cache = this.cache.get(
        new CacheKey({
          scope: getTokenOptions.scope || this.scope,
          audience: getTokenOptions.audience || 'default',
          client_id: this.options.client_id
        }),
        300 // get a new token if within 300 seconds of expiring
      );

      return cache && cache.access_token;
    };

    // Check the cache before acquiring the lock to avoid the latency of
    // `lock.acquireLock` when the cache is populated.
    if (!ignoreCache) {
      let accessToken = getAccessTokenFromCache();
      if (accessToken) {
        return accessToken;
      }
    }

    if (
      await retryPromise(
        () => lock.acquireLock(GET_ACCESS_TOKEN_LOCK_KEY, 5000),
        10
      )
    ) {
      try {
        // Check the cache a second time, because it may have been populated
        // by a previous call while this call was waiting to acquire the lock.
        if (!ignoreCache) {
          let accessToken = getAccessTokenFromCache();
          if (accessToken) {
            return accessToken;
          }
        }

        const authResult = await this._getTokenUsingRefreshToken(getTokenOptions);

        this.cache.save({ client_id: this.options.client_id, ...authResult });

        this.cookieStorage.save('da-auth.is.authenticated', true, {
          daysUntilExpire: this.sessionCheckExpiryDays
        });

        return authResult.access_token;
      } finally {
        await lock.releaseLock(GET_ACCESS_TOKEN_LOCK_KEY);
      }
    } else {
      throw new TimeoutError();
    }
  }

  private async _getTokenUsingRefreshToken(
    options: GetAccessTokenOptions
  ): Promise<any> {
    options.scope = options.scope || this.scope || this.defaultScope;

    // Get the full cache entry first (without expiry adjustment to get user_info)
    const fullCache = this.cache.get(
      new CacheKey({
        scope: options.scope,
        audience: options.audience || 'default',
        client_id: this.options.client_id
      }),
      -999999 // Large negative number to get cached entry even if expired
    );

    const cache = this.cache.get(
      new CacheKey({
        scope: options.scope,
        audience: options.audience || 'default',
        client_id: this.options.client_id
      })
    );

    const redirect_uri =
      options.redirect_uri ||
      this.options.redirect_uri ||
      window.location.origin;

    let tokenResult: IAuthTokenResponse | undefined;

    const {
      scope,
      audience,
      ignoreCache,
      timeoutInSeconds,
      ...customOptions
    } = options;

    const timeout =
      typeof options.timeoutInSeconds === 'number'
        ? options.timeoutInSeconds * 1000
        : null;

    try {
      tokenResult = await authenticationApi.get_refresh_auth_token(
        {
          access_token: cache && cache.access_token,
          refresh_token: cache && cache.refresh_token,
        } as RefreshTokenOptions
      );
    } catch (e) {
      if (
        // The refresh token is not found in memory, or,
        // the refresh token was found, but is it no longer valid.
        // redirect to Login view.
        // @ts-ignore
        e !== undefined && (e.message === MISSING_REFRESH_TOKEN_ERROR_MESSAGE ||
        // @ts-ignore
        (e.message && e.message.indexOf(INVALID_REFRESH_TOKEN_ERROR_MESSAGE) > -1))
      ) {
        // Missing or Invalid refresh token, redirect to login
        const url = this._url(`/login?`);
        window.location.assign(url);
      }

      throw e;
    }

    // Preserve user_info from cache if not returned by refresh endpoint
    const user_info = tokenResult?.user_info || fullCache?.user_info;

    return {
      ...tokenResult,
      user_info,
      scope: options.scope,
      audience: options.audience || 'default'
    };
  }

  /**
   * ```js
   * const isAuthenticated = await authClient.isAuthenticated();
   * ```
   *
   * Returns `true` if there's valid information stored,
   * otherwise returns `false`.
   *
   */
  public async isAuthenticated() {
    const user = await this.getUser();
    return !!user;
  }

  /**
   * ```js
   * await authClient.buildLogoutUrl(options);
   * ```
   *
   * Builds a URL to the logout endpoint using the parameters provided as arguments.
   * @param options
   */
  public buildLogoutUrl(options: LogoutUrlOptions = {}): string {
    if (options.client_id !== null) {
      options.client_id = options.client_id || this.options.client_id;
    } else {
      delete options.client_id;
    }

    const { federated, ...logoutOptions } = options;
    const federatedQuery = federated ? `&federated` : '';
    const url = this._url(`/v2/logout?${daUtils.createQueryParams(logoutOptions)}`);

    return url + federatedQuery;
  }

  /**
   * ```js
   * authClient.logout();
   * ```
   *
   * Clears the application session and performs a redirect to `/v2/logout`, using
   * the parameters provided as arguments, to clear the Auth0 session.
   * If the `federated` option is specified it also clears the Identity Provider session.
   * If the `localOnly` option is specified, it only clears the application session.
   * It is invalid to set both the `federated` and `localOnly` options to `true`,
   * and an error will be thrown if you do.
   * [Read more about how Logout works at Auth0](https://auth0.com/docs/logout).
   *
   * @param options
   */
  public logout(options: LogoutOptions = {}) {
    const { localOnly, ...logoutOptions } = options;

    if (localOnly && logoutOptions.federated) {
      throw new Error(
        'It is invalid to set both the `federated` and `localOnly` options to `true`'
      );
    }

    this.cache.clear();
    this.cookieStorage.remove('da-auth.is.authenticated');

    if (localOnly) {
      return;
    }
    const url = this.buildLogoutUrl(logoutOptions);
    window.location.assign(url);
  }

    /**
   * ```js
   * await authClient.signup(options);
   * ```
   *
   * Performs a redirect to `/authorize` using the parameters
   * provided as arguments. Random and secure `state` and `nonce`
   * parameters will be auto-generated.
   *
   * @param options
   */
  public async signup(options: AuthSignupOptions = {}): Promise<AuthSignupResult | undefined> {
    let resp = undefined;

    try {
      const signupResult = await authenticationApi.signup(options)

      if (signupResult && signupResult.success) {
        resp = {
          state: "success",
          signupResult: signupResult.data
        }
      } else {
        let msg = "SignupError";
        if (signupResult && signupResult.message && signupResult.message.toLowerCase().includes("invalidpassword")) {
          msg = "InvalidPasswordError";
        }
        resp = {
          state: "error",
          message: msg
        };
      }

      return resp;
    } catch (error) {
      //Network error
      let msg = "";

      if (typeof error === "string") {
          msg = error // works, `e` narrowed to string
      } else if (error instanceof Error) {
          msg = error.message // works, `e` narrowed to Error
      }

      throw error;
    }

  }

  /**
   * ```js
   * await authClient.signupConfirm(options);
   * ```
   *
   * Performs a redirect to `/authorize` using the parameters
   * provided as arguments. Random and secure `state` and `nonce`
   * parameters will be auto-generated.
   *
   * @param options
   */
  public async signupConfirm(options: AuthConfirmSignupOptions = {}): Promise<AuthConfirmSignupResult | undefined> {
    let resp = undefined;

    try {
      const signupResult = await authenticationApi.signup_confirm(options);

      if (signupResult && signupResult.success) {
        resp = {
          state: "success",
        }
      } else {
        let msg = "ConfirmSignupError";
        if (signupResult && signupResult.message &&
            (signupResult.message.toLowerCase().includes("codemismatch") || signupResult.message.toLowerCase().includes("expiredcode"))
        ) {
          msg = "InvalidCodeError";
        }
        resp = {
          state: "error",
          message: msg
        };
      }

      return resp;
    } catch (error) {
      //Network error
      let msg = "";

      if (typeof error === "string") {
          msg = error // works, `e` narrowed to string
      } else if (error instanceof Error) {
          msg = error.message // works, `e` narrowed to Error
      }

      throw error;
    }

  }

      /**
   * ```js
   * await authClient.passwordReset(options);
   * ```
   *
   * Performs a redirect to `/authorize` using the parameters
   * provided as arguments. Random and secure `state` and `nonce`
   * parameters will be auto-generated.
   *
   * @param options
   */
  public async passwordReset(options: AuthPasswordResetOptions = {}): Promise<AuthPasswordResetResult | undefined> {
    let resp = undefined;

    try {
      const resetResult = await authenticationApi.password_reset(options)

      if (resetResult && resetResult.success) {
        resp = {
          state: "success",
          passwordResetResult: resetResult.data
        }
      } else {
        let msg = "SignupError";
        if (resetResult && resetResult.message && resetResult.message.toLowerCase().includes("invalidpassword")) {
          msg = "InvalidPasswordError";
        }
        resp = {
          state: "error",
          message: msg
        };
      }

      return resp;
    } catch (error) {
      //Network error
      let msg = "";

      if (typeof error === "string") {
          msg = error // works, `e` narrowed to string
      } else if (error instanceof Error) {
          msg = error.message // works, `e` narrowed to Error
      }

      throw error;
    }

  }

  /**
   * ```js
   * await authClient.passwordResetConfirm(options);
   * ```
   *
   * Performs a redirect to `/authorize` using the parameters
   * provided as arguments. Random and secure `state` and `nonce`
   * parameters will be auto-generated.
   *
   * @param options
   */
  public async passwordResetConfirm(options: AuthConfirmPasswordResetOptions = {}): Promise<AuthConfirmPasswordResetResult | undefined> {
    let resp = undefined;

    try {
      const resetResult = await authenticationApi.password_reset_confirm(options);

      if (resetResult && resetResult.success) {
        resp = {
          state: "success",
        }
      } else {
        let msg = "ConfirmSignupError";
        if (resetResult && resetResult.message &&
            (resetResult.message.toLowerCase().includes("codemismatch") || resetResult.message.toLowerCase().includes("expiredcode"))
        ) {
          msg = "InvalidCodeError";
        }
        resp = {
          state: "error",
          message: msg
        };
      }

      return resp;
    } catch (error) {
      //Network error
      let msg = "";

      if (typeof error === "string") {
          msg = error // works, `e` narrowed to string
      } else if (error instanceof Error) {
          msg = error.message // works, `e` narrowed to Error
      }

      throw error;
    }

  }

  /**
   * ```js
   * await authClient.toggleNavigation();
   * ```
   *
   * Toggles the navigation flag. Remove the cookie if it exists, otherwise set it to false.
   *
   */
  public async toggleNavigation(): Promise<void> {

    if (this.cookieStorage.get('da-auth.can.navigate') === false) {
      this.cookieStorage.remove('da-auth.can.navigate');
    } else {
      this.cookieStorage.save('da-auth.can.navigate', false, {
        daysUntilExpire: this.sessionCheckExpiryDays
      });
    }
    return;
  }

  /**
   * ```js
   * const canNavigate = await authClient.canNavigate();
   * ```
   *
   * Returns false if the navigation flag is set to false, otherwise returns true.
   *
   */
  public async canNavigate(): Promise<boolean> {
    if (this.cookieStorage.get('da-auth.can.navigate') === false) {
      return false;
    }
    return true;
  }

}
